import { useState } from "react";
import { motion } from "framer-motion";
import { ThumbsUp, ThumbsDown, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { recommendFeedback } from "@/lib/api";

// Shown once per PAID submission. Dismissible; never blocks the feedback.
export const RecommendPrompt = ({ jobId }) => {
  const KEY = `sm_recommend_${jobId}`;
  const [done, setDone] = useState(() => {
    try { return !!localStorage.getItem(KEY); } catch { return false; }
  });
  const [vote, setVote] = useState(null); // 'up' | 'down'
  const [comment, setComment] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const finish = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* ignore */ }
    setDone(true);
  };

  const castVote = async (v) => {
    setVote(v);
    try {
      await recommendFeedback(jobId, { value: v, comment: "", share_consent: false });
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("recommend vote failed", e);
    }
  };

  const submitComment = async () => {
    setSaving(true);
    try {
      await recommendFeedback(jobId, {
        value: vote,
        comment: comment.trim(),
        share_consent: vote === "up" ? consent : false,
      });
      toast.success("Thank you — that's really helpful.");
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.error("recommend comment failed", e);
    }
    setSaving(false);
    finish();
  };

  if (done) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="mt-8 rounded-2xl border border-clay/30 bg-clay/[0.04] p-6 relative"
      data-testid="recommend-prompt"
    >
      <button
        type="button"
        onClick={finish}
        data-testid="recommend-dismiss"
        aria-label="Dismiss"
        className="absolute top-4 right-4 text-ink-faint hover:text-ink"
      >
        <X className="h-4 w-4" />
      </button>

      {!vote ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <h2 className="font-heading text-lg text-ink">Would you recommend StatementMate?</h2>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => castVote("up")}
              data-testid="recommend-up"
              variant="outline"
              className="border-forest/40 text-forest hover:bg-forest/10 rounded-lg px-4 py-5"
            >
              <ThumbsUp className="h-4 w-4 mr-2" /> Yes
            </Button>
            <Button
              type="button"
              onClick={() => castVote("down")}
              data-testid="recommend-down"
              variant="outline"
              className="border-border text-ink-muted hover:bg-sand-deep rounded-lg px-4 py-5"
            >
              <ThumbsDown className="h-4 w-4 mr-2" /> Not really
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4" data-testid="recommend-followup">
          <p className="font-medium text-ink">
            {vote === "up"
              ? "Glad it helped! Would you share a sentence on what was useful?"
              : "Sorry to hear that — what could be better?"}
          </p>
          <textarea
            data-testid="recommend-comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional"
            className="w-full bg-white border border-border focus:border-clay focus:ring-1 focus:ring-clay rounded-xl p-4 text-sm resize-y min-h-[90px] outline-none"
          />
          {vote === "up" && (
            <label className="flex items-start gap-3 cursor-pointer" data-testid="recommend-consent-label">
              <Checkbox
                checked={consent}
                onCheckedChange={(v) => setConsent(v === true)}
                data-testid="recommend-consent"
                className="mt-0.5 data-[state=checked]:bg-clay data-[state=checked]:border-clay"
              />
              <span className="text-sm text-ink-soft leading-relaxed">
                I'm happy for this comment to be shared anonymously to help other students.
              </span>
            </label>
          )}
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={submitComment}
              disabled={saving}
              data-testid="recommend-send"
              className="bg-clay hover:bg-clay-hover text-white rounded-lg px-6 py-5"
            >
              Send
            </Button>
            <button
              type="button"
              onClick={finish}
              data-testid="recommend-skip"
              className="text-sm text-ink-muted hover:text-ink"
            >
              No thanks
            </button>
          </div>
        </div>
      )}
    </motion.section>
  );
};
